import React, { useState, useEffect } from 'react';
import SyncLoader from 'react-spinners/SyncLoader';
import { nFormat } from '../../data/formatters';
import { CROPS } from '../../data/crops';
import { cropSuitabilityScale } from '../../data/suitabilityScales';
import { theme } from '../../theme/theme';
import { useTranslation } from 'react-i18next';

interface Product {
  id: number; code: number; name: string; image: string;
  suitability: string; suitabilityIndex: string; attainableYield: string;
}

interface Props {
  allCropSuitability: any[];
  allCropDataError: boolean;
  cropCode: string;
}

export const AlternateCropsTable: React.FC<Props> = ({ allCropSuitability, allCropDataError, cropCode }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);

  const buildProducts = () => {
    const sorted = [...allCropSuitability].sort((a, b) => a.cropSuitability - b.cropSuitability);
    const filtered = sorted.filter((item) => item.cropSuitability < 8 && item.cropCode !== cropCode);
    return filtered.map((crop) => {
      const scale = cropSuitabilityScale[crop.suitability];
      const suitabilityIndex = scale ? `${scale[2]} < SI < ${scale[3]}` : 'N/A';
      const attainableYield = `${nFormat.format(Math.round(Number(crop.attainableYield))).replace(/,/g, ' ')} kg/ha`;
      return {
        id: CROPS[crop.cropName]?.ecocropID,
        code: CROPS[crop.cropName]?.ecocropID,
        name: crop.cropName,
        scientific: CROPS[crop.cropName]?.scientificName,
        image: `${crop.cropName}.jpg`,
        suitability: crop.suitability,
        suitabilityIndex,
        attainableYield,
      };
    });
  };

  useEffect(() => { setProducts(buildProducts()); }, [allCropDataError]);

  return (
    <div className="card">
      {allCropDataError && (
        <div className="flex w-full flex-column align-items-center justify-content-center p-2 m-2">
          <p>{t('alternateCrops.loading')}</p>
          <SyncLoader size={5} aria-label="Loading Spinner" data-testid="loader" />
        </div>
      )}
      {!allCropDataError && (
        <div className="border-1 p-1 surface-border border-round m-1 align-items-center" style={{ width: '100%' }}>
          {products.length > 0
            ? <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #ccc', width: '100%', color: theme.colors.text.onLight }}>
                {([
                  { name: t('alternateCrops.name'), scientific: t('alternateCrops.scientific'), suitability: t('alternateCrops.suitability'), suitabilityIndex: t('alternateCrops.index'), attainableYield: t('alternateCrops.attainableYield'), header: true },
                  ...products.map(p => ({ ...p, header: false })),
                ] as { name: string; scientific: string; suitability: string; suitabilityIndex: string; attainableYield: string; header: boolean }[]).map(({ name, scientific, suitability, suitabilityIndex, attainableYield, header }, i, arr) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      fontWeight: header ? 'bold' : undefined,
                      background: header ? '#f5f5f5' : i % 2 === 0 ? undefined : '#fafafa',
                      borderBottom: `1px solid ${header ? '#ccc' : i < arr.length - 1 ? '#eee' : 'transparent'}`,
                      fontSize: header ? theme.typography.fontSizeBase : theme.typography.fontSizeSm,
                    }}
                  >
                    <div style={{ flex: '1', padding: '5px', borderRight: '1px solid #ccc' }}>{name} <i>({scientific})</i></div>
                    <div style={{ flex: '0 0 18%', padding: '5px', borderRight: '1px solid #ccc', textAlign: 'center' }}>{suitability}</div>
                    <div style={{ flex: '0 0 16%', padding: '5px', borderRight: '1px solid #ccc', textAlign: 'center' }}>{suitabilityIndex}</div>
                    <div style={{ flex: '0 0 16%', padding: '5px' }}>{attainableYield}</div>
                  </div>
                ))}
              </div>
            : <p>{t('alternateCrops.noSuitable')}</p>
          }
        </div>
      )}
    </div>
  );
};
